import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  FolderOpenIcon,
  DocumentPlusIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  classifyUploadFiles,
  uploadIdentityItems,
  ALLOWED_UPLOAD_EXTENSIONS,
  MAX_UPLOAD_BYTES,
} from '../moduleServices/uploadIdentityFiles';

const statusDot = (item) => {
  if (!item.ok && !item.uploadStatus) return 'bg-amber-400';
  switch (item.uploadStatus) {
    case 'uploading':
      return 'bg-module-identity/70 animate-pulse';
    case 'ok':
      return 'bg-green-500';
    case 'error':
      return 'bg-red-500';
    case 'skipped':
      return 'bg-gray-400';
    default:
      return item.ok ? 'bg-green-400' : 'bg-amber-400';
  }
};

const StatusIcon = ({ item }) => {
  if (item.uploadStatus === 'ok') {
    return <CheckCircleIcon className="h-4 w-4 text-green-500 shrink-0" />;
  }
  if (item.uploadStatus === 'error') {
    return <ExclamationCircleIcon className="h-4 w-4 text-red-500 shrink-0" />;
  }
  if (item.uploadStatus === 'skipped') {
    return <MinusCircleIcon className="h-4 w-4 text-gray-400 shrink-0" />;
  }
  if (item.uploadStatus === 'uploading') {
    return <ArrowPathIcon className="h-4 w-4 text-module-identity shrink-0 animate-spin" />;
  }
  return (
    <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${statusDot(item)}`} />
  );
};

const UploadModal = ({ isOpen, onClose, folderPrefix = '', onUploaded }) => {
  const [items, setItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setItems([]);
    setIsUploading(false);
    setProgress({ done: 0, total: 0 });
    setIsDragging(false);
  }, [isOpen, folderPrefix]);

  const ingestFiles = useCallback((fileList) => {
    const classified = classifyUploadFiles(fileList, folderPrefix);
    if (!classified.length) return;
    setItems((prev) => {
      const byPath = new Map(prev.map((i) => [i.path || `${i.file.name}-${i.reason}`, i]));
      classified.forEach((c) => {
        const key = c.path || `${c.file.name}-${c.reason}`;
        byPath.set(key, { ...c, uploadStatus: null, uploadError: null });
      });
      return [...byPath.values()].sort((a, b) => (a.path || '').localeCompare(b.path || ''));
    });
  }, [folderPrefix]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) ingestFiles(e.dataTransfer.files);
  }, [ingestFiles]);

  const handleUpload = async () => {
    const accepted = items.filter((i) => i.ok);
    if (!accepted.length) {
      toast.error('No valid files to upload');
      return;
    }
    setIsUploading(true);
    setProgress({ done: 0, total: accepted.length });
    setItems((prev) => prev.map((i) => (
      i.ok ? { ...i, uploadStatus: 'pending', uploadError: null } : i
    )));

    try {
      const summary = await uploadIdentityItems(accepted, {
        onProgress: ({ done, total }) => setProgress({ done, total }),
        onItemStatus: ({ path, status, error }) => {
          setItems((prev) => prev.map((i) => (
            i.path === path
              ? { ...i, uploadStatus: status, uploadError: error || null }
              : i
          )));
        },
      });

      if (summary.succeeded > 0) {
        toast.success(`Uploaded ${summary.succeeded} file${summary.succeeded === 1 ? '' : 's'}`);
        onUploaded?.(summary);
      }
      if (summary.aborted) {
        toast.error('GCS permission denied — upload aborted. Fix local ADC / SA and retry.');
      } else if (summary.failed > 0) {
        toast.error(`${summary.failed} file${summary.failed === 1 ? '' : 's'} failed`);
      }

      if (summary.failed > 0 || summary.aborted) {
        const failedPaths = new Set(
          summary.outcomes.filter((o) => !o.ok).map((o) => o.path),
        );
        setItems((prev) => prev.filter((i) => !i.ok || failedPaths.has(i.path)));
      } else {
        onClose();
      }
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const acceptedCount = items.filter((i) => i.ok).length;
  const skippedCount = items.filter((i) => !i.ok).length;
  const destLabel = folderPrefix ? `${folderPrefix}/` : '(root)';
  const pct = progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={isUploading ? undefined : onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Upload to Identity</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Destination: <span className="font-mono text-gray-600">{destLabel}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="p-1 hover:bg-gray-100 rounded-lg disabled:opacity-40"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-5 pb-3 space-y-3 overflow-y-auto flex-1">
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
              isDragging
                ? 'border-module-identity bg-module-identity/10'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-600">Drop files or a folder here</p>
            <p className="text-xs text-gray-400 mt-1">
              Allowed: {[...ALLOWED_UPLOAD_EXTENSIONS].join(', ')} · max {MAX_UPLOAD_BYTES / 1024} KB each
            </p>
            <div className="flex justify-center gap-2 mt-4">
              <button
                type="button"
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
              >
                <DocumentPlusIcon className="h-4 w-4" />
                Files
              </button>
              <button
                type="button"
                disabled={isUploading}
                onClick={() => folderInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
              >
                <FolderOpenIcon className="h-4 w-4" />
                Folder
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".md,.json,.txt,text/markdown,application/json,text/plain"
            className="hidden"
            onChange={(e) => {
              ingestFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            className="hidden"
            multiple
            {...{ webkitdirectory: '', directory: '' }}
            onChange={(e) => {
              ingestFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {items.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5 px-0.5">
                <p className="text-xs font-medium text-gray-500">
                  {acceptedCount} ready
                  {skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}
                  {isUploading ? ` · ${progress.done}/${progress.total}` : ''}
                </p>
                {!isUploading && (
                  <button
                    type="button"
                    onClick={() => setItems([])}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                )}
              </div>

              {(isUploading || progress.total > 0) && (
                <div className="mb-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full bg-module-identity transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}

              <ul className="max-h-56 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
                {items.map((item) => (
                  <li
                    key={`${item.path}-${item.file.name}-${item.reason || 'ok'}`}
                    className="px-3 py-2 text-xs flex items-start gap-2"
                  >
                    <StatusIcon item={item} />
                    <div className="min-w-0 flex-1">
                      <p className={`font-mono truncate ${
                        item.uploadStatus === 'error'
                          ? 'text-red-700'
                          : item.uploadStatus === 'ok'
                            ? 'text-green-700'
                            : item.ok
                              ? 'text-gray-700'
                              : 'text-gray-400'
                      }`}
                      >
                        {item.path || item.file.name}
                      </p>
                      {!item.ok && item.reason && (
                        <p className="text-amber-600 mt-0.5">{item.reason}</p>
                      )}
                      {item.uploadError && (
                        <p className="text-red-600 mt-0.5 break-words">{item.uploadError}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || acceptedCount === 0}
            className="flex-1 px-4 py-2 bg-module-identity text-white rounded-lg text-sm font-medium hover:bg-module-identity/90 disabled:opacity-50"
          >
            {isUploading
              ? `Uploading ${progress.done}/${progress.total}…`
              : `Upload ${acceptedCount || ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
